// @flow
import * as csx from 'csstips';
import classify from '@src/utils/typestyle';
import classnames from 'classnames';
import { formatPrice } from '@src/utils/general';
import { shapeNames } from '@src/utils/displayMaps';
import { trackSelectItem } from '@src/utils/googleAnalytics';

import FavoriteIcon from '@components/FavoriteIcon';

import type { OnRowSelectionType } from '@components/containers/Diamonds';
import type { DiamondType } from '@types/diamond';

import { regularGrayText } from '@src/styles/general';
import colors from '@src/constants/colors';

const shapeImages = {
  B: '/images/diamond_search_results/b-hover.svg',
  PR: '/images/diamond_search_results/pr-hover.svg',
  O: '/images/diamond_search_results/o-hover.svg',
  M: '/images/diamond_search_results/m-hover.svg',
  P: '/images/diamond_search_results/p-hover.svg',
  C: '/images/diamond_search_results/c-hover.svg',
  E: '/images/diamond_search_results/e-hover.svg',
  AS: '/images/diamond_search_results/as-hover.svg',
  R: '/images/diamond_search_results/r-hover.svg',
  H: '/images/diamond_search_results/h-hover.svg'
};

export const columnWidths = {
  compare: 75,
  shape: 98,
  carat: 54,
  cut: 94,
  color: 42,
  clarity: 66,
  polish: 90,
  symmetry: 95,
  fluorescence: 106,
  depth: 66,
  table: 63,
  lw_ratio: 63,
  price: 90
};

const classes = classify({
  rowContainer: {
    ...csx.horizontal,
    color: `${regularGrayText} !important`,
    cursor: 'pointer',
    height: '42px',
    fontSize: '14px',
    lineHeight: '40px',
    textAlign: 'center',
    $nest: {
      '&:hover': {
        background: '#f6e5b8'
      }
    }
  },
  selectedRowContainer: {
    background: '#f6e5b8'
  },
  column: {
    float: 'left',
    borderBottom: '1px solid #efefef'
  },
  price: {
    minWidth: columnWidths.price
  },
  seePhotoLink: {
    color: colors.lumeraMidBlue,
    textDecoration: 'underline',
    $nest: {
      '&:hover': {
        color: colors.lumeraMidBlue,
      },
    },
  },
});

type PropsType = {
  diamond: DiamondType,
  onSelection: OnRowSelectionType,
  selected: boolean,
  responsiveColumnClasses: { [string]: string },
  dyor: boolean
};

function SeePhotoLink({ diamond }: { diamond: DiamondType }) {
  const { vendor_image: vendorImage } = diamond;

  if (!vendorImage) {
    return null;
  }

  return (
    <a
      href={vendorImage}
      onClick={(e) => e.stopPropagation()}
      className={classes.seePhotoLink}
      target='_blank'
    >
      see photo
    </a>
  );
}

export default class DiamondDatagridRow extends React.Component<PropsType> {
  handleRowSelection = () => {
    this.props.onSelection(this.props.diamond.list_service_diamond_id);
  };

  location = () => {
    const { diamond } = this.props;
    return `/${this.props.dyor ? '' : 'loose-'}diamonds/detail/${
      diamond.list_service_diamond_id
    }`;
  };

  navigateToDiamondDetail = () => {
    window.location = this.location();
  }

  handleClick = () => {
    if (window) {
      trackSelectItem(this.props.diamond);
      this.navigateToDiamondDetail();
    }
  };

  render() {
    return (
      <div
        onClick={this.handleClick}
        className={classnames(classes.rowContainer, {
          [classes.selectedRowContainer]: this.props.selected
        })}
        data-test-name='result-row'
      >
        <div
          className={classnames(classes.column, classes.compare)}
          style={{ width: columnWidths.compare }}
          >
          <FavoriteIcon
            className={classes.compare}
            onClick={this.handleRowSelection}
            selected={this.props.selected}
            />
        </div>
        <div
          className={classnames(classes.column, classes.shape)}
          // Pad the left when we hide the compare column
          style={{
            width: columnWidths.shape,
            textAlign: 'left',
            paddingLeft: 0
          }}
        >
          <img
            src={shapeImages[this.props.diamond.shape]}
            style={{ height: '22px', marginRight: '5px', marginTop: '-2px' }}
            alt={`Thumbnail of ${shapeNames[this.props.diamond.shape]} diamond`}
          />
          <SeePhotoLink diamond={this.props.diamond} />
        </div>
        <div
          className={classnames(classes.column, classes.carat)}
          style={{ width: columnWidths.carat }}
        >
          {parseFloat(this.props.diamond.carat).toFixed(2)}
        </div>
        <div
          className={classnames(classes.column, classes.cut)}
          style={{ width: columnWidths.cut }}
        >
          {this.props.diamond.final_cut_grade || 'N/A'}
        </div>
        <div
          className={classnames(classes.column, classes.color)}
          style={{ width: columnWidths.color }}
        >
          {this.props.diamond.color}
        </div>
        <div
          className={classnames(classes.column, classes.clarity)}
          style={{ width: columnWidths.clarity }}
        >
          {this.props.diamond.clarity}
        </div>
        <div
          className={classnames(
            classes.column,
            classes.polish,
            this.props.responsiveColumnClasses.polish
          )}
          style={{ width: columnWidths.polish }}
        >
          {this.props.diamond.vendor_polish}
        </div>
        <div
          className={classnames(
            classes.column,
            classes.symmetry,
            this.props.responsiveColumnClasses.symmetry
          )}
          style={{ width: columnWidths.symmetry }}
        >
          {this.props.diamond.vendor_symmetry}
        </div>
        <div
          className={classnames(
            classes.column,
            classes.fluorescence,
            this.props.responsiveColumnClasses.fluorescence
          )}
          style={{ width: columnWidths.fluorescence }}
        >
          {this.props.diamond.vendor_fluorescence}
        </div>
        <div
          className={classnames(
            classes.column,
            classes.depth,
            this.props.responsiveColumnClasses.depth
          )}
          style={{ width: columnWidths.depth }}
        >
          {this.props.diamond.vendor_depth}%
        </div>
        <div
          className={classnames(
            classes.column,
            classes.table,
            this.props.responsiveColumnClasses.table
          )}
          style={{ width: columnWidths.table }}
        >
          {this.props.diamond.vendor_table}%
        </div>
        <div
          className={classnames(
            classes.column,
            classes.lw_ratio,
            this.props.responsiveColumnClasses.lw_ratio
          )}
          style={{ width: columnWidths.lw_ratio }}
        >
          {parseFloat(this.props.diamond.length_width_ratio).toFixed(2)}
        </div>
        <div
          className={classnames(
            classes.column,
            classes.price,
            this.props.responsiveColumnClasses.price
          )}
          style={{ textAlign: 'right', paddingRight: '25px' }}
        >
          {formatPrice(this.props.diamond.retail_price)}
        </div>
      </div>
    );
  }
}
